import { useState, useEffect, useCallback, useRef } from "react";
import { useDialogFocus } from "../hooks/useDialogFocus";
import { CloseIcon } from "../assets/CloseIcon";
import { ModalCloseButton } from "./ModalCloseButton";
import "./JerseyNumberModal.css";

interface JerseyNumberModalProps {
  isOpen: boolean;
  teamId: string;
  teamName: string;
  prospectName: string;
  currentJerseyNumber: number | null;
  onSave: (jerseyNumber: number | null) => void;
  onClose: () => void;
}

export function JerseyNumberModal({
  isOpen,
  teamName,
  prospectName,
  currentJerseyNumber,
  onSave,
  onClose,
}: JerseyNumberModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: Boolean(isOpen), dialogRef, handleEscape: false });

  const [jerseyText, setJerseyText] = useState<string>("");

  // Reset jersey text when modal opens
  useEffect(() => {

    if (isOpen) {
      setJerseyText(currentJerseyNumber?.toString() ?? "");
    }
  }, [isOpen, currentJerseyNumber]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
  }, [isOpen, onClose]);

  // Handle save
  const handleSave = useCallback(() => {
    const trimmed = jerseyText.trim();
    if (trimmed === "") {
      onSave(null);
    } else {
      const num = parseInt(trimmed, 10);
      if (!isNaN(num) && num >= 0 && num <= 99) {
        onSave(num);
      } else {
        onSave(null);
      }
    }
  }, [jerseyText, onSave]);

  // Handle ENTER key to save
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSave();
      }
    },
    [handleSave]
  );

  // Handle clear
  const handleClear = useCallback(() => {
    setJerseyText("");
  }, []);

  // Handle overlay click
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const numericText = e.target.value.replace(/[^0-9]/g, "");
      if (numericText.length <= 2) {
        setJerseyText(numericText);
      }
    },
    []
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="jersey-modal__overlay" onClick={handleOverlayClick}>
      <div ref={dialogRef} tabIndex={-1}
        className="jersey-modal__content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="jersey-modal-title"
      >
        {/* Header */}
        <div className="jersey-modal__header">
          <div className="jersey-modal__header-content">
            <h3 id="jersey-modal-title" className="jersey-modal__title">
              Jersey Number
            </h3>
            <p className="jersey-modal__subtitle">
              {prospectName} - {teamName}
            </p>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Body */}
        <div className="jersey-modal__body">
          <div className="jersey-modal__input-container">
            <input
              type="text"
              inputMode="numeric"
              className="jersey-modal__input"
              value={jerseyText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter number (0-99)"
              autoFocus
              maxLength={2}
            />
            {jerseyText.length > 0 && (
              <button
                className="jersey-modal__clear-button"
                onClick={handleClear}
                aria-label="Clear input"
              >
                <CloseIcon />
              </button>
            )}
          </div>
          <p className="jersey-modal__hint">
            Leave empty to clear the jersey number
          </p>
        </div>

        {/* Footer */}
        <div className="jersey-modal__footer">
          <button className="jersey-modal__save-button" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
